import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useHotkeys } from 'react-hotkeys-hook'
import { UserDataContext, OfflineModeContext } from '../../Context'
import { calculateOOW } from '../../Pages/Dashboard/utils'
import { onSave } from '../../Pages/NewCase/Validations/saveAndValidate'
import { saveToDb } from '../../db'
import { formatData } from '../../Pages/NewCase/Validations/utils'
import { isValidDate } from '../../utils'
import { NeedsAttentionContext } from '../../Context/NeedsAttention.context'
import { request } from '../../api'
import { modalPresets } from '../../Components/CustomComponents/Modals'

const formatOfflineCaseData = async ({ formData, formList, mainCaseData }) => {
  const {
    adverseDrugData,
    assaultData,
    childPoisoningData,
    firearmInjuryData,
    motorVehicleData,
    selfInflictedData,
    workRelatedData,
  } = formData

  let specialStudiesList = []
  formList.forEach(async ({ id }) => {
    let formattedContent
    switch (id) {
      case 'firearmInjury':
        formattedContent = await formatData(firearmInjuryData)
        specialStudiesList.push({
          type: 'firearm injuries',
          content: { ...formattedContent },
        })
        break
      case 'workRelated':
        formattedContent = await formatData(workRelatedData)
        specialStudiesList.push({
          type: 'NIOSH work injuries',
          content: { ...formattedContent },
        })
        break
      case 'childPoisoning':
        formattedContent = await formatData(childPoisoningData)
        specialStudiesList.push({
          type: "children's poisoning",
          content: { ...formattedContent },
        })
        break
      case 'selfInflicted':
        formattedContent = await formatData(selfInflictedData)
        specialStudiesList.push({
          type: 'self-inflicted injuries',
          content: { ...formattedContent },
        })
        break
      case 'adverseDrug':
        formattedContent = await formatData(adverseDrugData)
        specialStudiesList.push({
          type: 'adverse drug event',
          content: { ...formattedContent },
        })
        break
      case 'assault':
        formattedContent = await formatData(assaultData)
        specialStudiesList.push({
          type: 'assaults',
          content: { ...formattedContent },
        })
        break
      case 'motorVehicle':
        formattedContent = await formatData(motorVehicleData)
        specialStudiesList.push({
          type: 'NHTSA non-crash motor vehicle injuries',
          content: { ...formattedContent },
        })
        break
      default:
        break
    }
  })

  const formattedMainCase = await formatData(mainCaseData)

  if (specialStudiesList.length > 0) {
    return {
      ...formattedMainCase,
      specialStudies: [...specialStudiesList],
    }
  } else {
    return {
      ...formattedMainCase,
    }
  }
}

const SaveButton = ({
  args,
  buttonType,
  saveArgs,
  sendReply,
  prodList,
  lastUpdateableDate,
  toggleDeleteModal,
  toggleVictimIdButtonErrors,
  submitCases,
  setCases,
  selectedCases,
  setSelectedCases,
  replyMsg,
  resetForm,
  errorId,
  setReplyMsg,
  handleReturnedErrors

}) => {
  const [isOowW, setIsOowW] = useState(false)
  const [OowChecked, setoowChecked] = useState(false)
  const [status, setStatus] = useState('')
  const {
    handleAlerts,
    formData,
    formList,
    setButtonType,
    setUnsavedChanges,
    options,
  } = saveArgs
  const { mainCaseData, firearmInjuryData } = formData
  const {
    caseNumber,
    treatmentDate,
  } = mainCaseData
  const { dateInjured } = firearmInjuryData
  const { userData, setUserData } = useContext(UserDataContext)
  const { offlineMode } = useContext(OfflineModeContext)
  const { oowValid } = userData
  const { existingCaseId } = useParams()

  let indexedData
  let formatTreatmentDate

  // format treatmentDate to yyyy-mm-dd to compare against lastUpdateableDate.
  if (treatmentDate) {
    formatTreatmentDate = new Date(treatmentDate).toISOString().split('T')[0]
  }

  // assign a random number to the case that's used to determine when to generate a victim id.
  if (mainCaseData.caseRate === undefined) {
    // random number: 0 <= num < 1, with two decimal places
    mainCaseData.caseRate = Math.round(Math.random() * 100) / 100
  }

  const disableInputCheck = () => {
    if (
      !caseNumber ||
      !treatmentDate ||
      (treatmentDate && !isValidDate(treatmentDate)) ||
      (dateInjured && !isValidDate(dateInjured))
    ) {
      return true
    }
    if (existingCaseId && !oowValid && calculateOOW(mainCaseData.treatmentDate)) {
      return calculateOOW(formData.mainCaseData.treatmentDate)
    }
    return false
  }

  useEffect(() => {
    let submitCase = formData.mainCaseData
    if (mainCaseData.treatmentDate && existingCaseId && !OowChecked) {
      setIsOowW(calculateOOW(mainCaseData.treatmentDate))
      setoowChecked(true)
    }
    setStatus(mainCaseData.status)
    setSelectedCases([submitCase]) // buttom submit existing case within current form
    setUnsavedChanges(false)
  }, [
    existingCaseId,
    mainCaseData.treatmentDate,
    isOowW,
    OowChecked,
    setUnsavedChanges,
    formData.mainCaseData,
    mainCaseData.status,
    setSelectedCases,
  ])
  useHotkeys(
    'alt + ctrl + s, cmd + ctrl + s',
    async (event, handler) => {
      switch (handler.key) {
        case 'alt+ctrl+s':
        case 'cmd+ctrl+s':
          if (!disableInputCheck()) {
            const args = {
              ...saveArgs,
              options: {
                ...options,
                mainCaseOptions: {
                  ...options.mainCaseOptions,
                  caseProduct: prodList,
                },
              },
            }
            if (offlineMode) {
              const mainCaseData = await onSave({
                ...args,
                validateOnly: true,
              })
              indexedData = await formatOfflineCaseData({
                formData,
                formList,
                mainCaseData,
              })
              saveToDb({
                caseNumber: caseNumber,
                caseData: indexedData,
                setButtonType,
                setUnsavedChanges,
                handleAlerts,
              })
            } else {
              const summary = await onSave({
                ...args,
                validateOnly: offlineMode,
              })
              // update context with new summary numbers
              setUserData((prev) => {
                return {
                  ...prev,
                  hospital: {
                    ...prev.hospital,
                    ...summary,
                  },
                  hospitalData: prev.hospitalData.map((hData) => {
                    if (hData.groupId === prev.hospital.groupId) {
                      return {
                        ...hData,
                        ...summary,
                      }
                    }
                    return hData
                  }),
                }
              })
            }
          }
          break
        default:
          break
      }
    },
    { enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT'] },
  )

  // Conditionally send reply to /errors endpoint (after Save)
  const handleSendReply = async () => {
    if (replyMsg !== '') {
      try {
        await request({
          ...args,
          body: { reply: replyMsg },
          caseId: existingCaseId,
          def: 'returnedErrorMsgs',
          handleAlerts,
          method: 'PUT',
          queryString: errorId,
        })
        setReplyMsg('')
        const returnedErrors = await request({
          ...args,
          caseId: existingCaseId,
          def: 'returnedErrorMsgs',
          handleAlerts,
          method: 'GET',
        })
        handleReturnedErrors(returnedErrors)
        handleAlerts(modalPresets('Your reply has been sent.').success)
      } catch (err) {
        console.log('ERROR:', err.message)
      }
    }  
  }

  return (
    <>
      <div className='case-form__save-button-wrapper'>
        {buttonType === 'submit' && (
          <button
            type='button'
            className={`save-button button button--primary ${
              !existingCaseId ? '' : 'ut-margin-right-1-em'
            }`}
            onClick={async (e) => {
              const args = {
                ...saveArgs,
                options: {
                  ...options,
                  mainCaseOptions: {
                    ...options.mainCaseOptions,
                    caseProduct: prodList,
                  },
                },
              }
              toggleVictimIdButtonErrors()
              setUnsavedChanges(false)

              if (offlineMode) {
                const mainCaseData = await onSave({
                  ...args,
                  validateOnly: true,
                })
                indexedData = await formatOfflineCaseData({
                  formData,
                  formList,
                  mainCaseData,
                })
                saveToDb({
                  caseNumber: caseNumber,
                  caseData: indexedData,
                  setButtonType,
                  setUnsavedChanges,
                  handleAlerts,
                })
              } else {
                // get latest summary numbers from the server
                const summary = await onSave({
                  ...args,
                  validateOnly: offlineMode,
                  sendReply: handleSendReply
                })
                setUnsavedChanges(false)
                // update context with new summary numbers
                setUserData((prev) => {
                  return {
                    ...prev,
                    hospital: {
                      ...prev.hospital,
                      ...summary,
                    },
                    hospitalData: prev.hospitalData.map((hData) => {
                      if (hData.groupId === prev.hospital.groupId) {
                        return {
                          ...hData,
                          ...summary,
                        }
                      }
                      return hData
                    }),
                  }
                })
              }
            }}
            disabled={disableInputCheck()}
          >
            <span className='save-button-label'>
              {existingCaseId ? 'Save Case & Studies' : 'Save NEISS Case'}
            </span>
          </button>
        )}
        {buttonType === 'continue' && (
          <ContinueButton
            resetForm={resetForm}
            setUnsavedChanges={setUnsavedChanges}
          />
        )}
        {existingCaseId && status === 'ready to submit' && (
          <button
            type='button'
            className='save-button button button--primary button--narrow ut-margin-right-1-em'
            onClick={() => {
              submitCases({
                args: { ...args, handleAlerts },
                cases: selectedCases,
                getSummary: false,
                setCases,
                setModal: handleAlerts,
                setSelectedCases,
                setUserData,
                setStatus,
                userData,
              })
            }}
            disabled={disableInputCheck()}
          >
            Submit
          </button>
        )}
        {existingCaseId && formatTreatmentDate > lastUpdateableDate && (
          <button
            className='button button--secondary button--secondary-warning button--narrow'
            onClick={toggleDeleteModal}
            disabled={disableInputCheck()}
          >
            Delete
          </button>
        )}
      </div>
      {existingCaseId && (
        <div className='case-status-wrapper'>
          <span className='label'>Case Status:</span>
          {status === 'needs attention' && (
            <span className='label ut-color-red'>Needs Attention</span>
          )}
          {status === 'ready to submit' && status !== 'submitted' && (
            <span className='label ut-color-green'>Ready to Submit</span>
          )}
          {status === 'submitted' && (
            <span className='label ut-color-gray'>Submitted</span>
          )}
        </div>
      )}
    </>
  )
}

const ContinueButton = ({ resetForm, setUnsavedChanges }) => {
  const [needsAttentionContext] = useContext(NeedsAttentionContext)
  const { caseIds } = needsAttentionContext
  useHotkeys(
    'alt + ctrl + c, cmd + ctrl + c',
    (event, handler) => {
      switch (handler.key) {
        case 'alt+ctrl+c':
        case 'cmd+ctrl+c':
          resetForm()
          break
        default:
          break
      }
    },
    { enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT'] },
  )

  useEffect(() => {
    setUnsavedChanges(false)
  }, [setUnsavedChanges])

  return (
    <div className='case-form__save-button-wrapper ut-margin-right-1-em'>
      <button
        type='button'
        className='save-button button button--primary'
        onClick={resetForm}
      >
        <span className='save-button-label'>
          {caseIds?.length <= 1 ? 'Create new case' : 'Continue to next case'}
        </span>
      </button>
    </div>
  )
}

export { SaveButton, ContinueButton }
